import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaPlane, 
  FaHotel, 
  FaHome, 
  FaCar, 
  FaCarSide,
  FaArrowRight,
  FaCheck,
  FaStar,
  FaQuoteLeft
} from 'react-icons/fa';
import Lottie from 'lottie-react';
import airplaneAnimation from '../assets/airplane.json'; // Include your animation file
import carAnimation from '../assets/car.json'; // Optional driving animation

const HomePage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    service: '',
    budget: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    {
      icon: FaPlane,
      title: 'Flight Booking',
      description: 'Domestic and international flights with the best rates and flexible booking options.',
      features: ['Best Price Guarantee', 'Flexible Dates', '24/7 Support'],
      path: '/flight-booking',
      color: 'bg-blue-500',
      image: 'https://images.unsplash.com/photo-1558204692-5f402fe220b9'
    },
    {
      icon: FaHotel,
      title: 'Hotel Booking',
      description: 'Comfortable accommodations from budget-friendly to luxury hotels across Nigeria.',
      features: ['Verified Hotels', 'Best Rates', 'Instant Confirmation'],
      path: '/hotel-booking',
      color: 'bg-purple-500',
      image: 'https://images.pexels.com/photos/14210678/pexels-photo-14210678.jpeg'
    },
    {
      icon: FaHome,
      title: 'Apartment Booking',
      description: 'Short-term and extended stay apartments for business and leisure travelers.',
      features: ['Furnished Options', 'Flexible Terms', 'Prime Locations'],
      path: '/apartment-booking',
      color: 'bg-orange-500',
      image: 'https://images.unsplash.com/photo-1716978497661-48bfa34130c0'
    },
    {
      icon: FaCarSide,
      title: 'Airport Pickup',
      description: 'Reliable airport transfer services with professional drivers and comfortable vehicles.',
      features: ['Professional Drivers', 'Flight Tracking', 'Meet & Greet'],
      path: '/airport-pickup',
      color: 'bg-green-500',
      image: 'https://images.pexels.com/photos/3536293/pexels-photo-3536293.jpeg'
    },
    {
      icon: FaCar,
      title: 'Car Rental',
      description: 'Self-drive and chauffeur-driven car rental services for all your transportation needs.',
      features: ['Modern Fleet', 'Competitive Rates', 'Insurance Included'],
      path: '/car-rental',
      color: 'bg-red-500',
      image: 'https://images.pexels.com/photos/3024008/pexels-photo-3024008.jpeg'
    }
  ];

  const testimonials = [
    {
      name: 'Adebayo Johnson',
      location: 'Lagos',
      rating: 5,
      text: 'Yowa Travels made my business trip to Abuja seamless. The flight booking was easy and the airport pickup was right on time.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150'
    },
    {
      name: 'Fatima Aliyu',
      location: 'Kano',
      rating: 5,
      text: 'Excellent service! They helped me find the perfect apartment in Lagos for my 3-month project. Highly recommended!',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150'
    },
    {
      name: 'Chike Okafor',
      location: 'Port Harcourt',
      rating: 5,
      text: 'The hotel recommendations were spot-on. Great value for money and the booking process was incredibly smooth.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000); // Reset after 5 seconds
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1719314073622-9399d167725b')`,
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        {/* Decorative Animation Top Right */}
        <motion.div 
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 2 }}
          className="absolute top-10 right-10 w-28 sm:w-40"
        >
          <Lottie animationData={airplaneAnimation} loop={true} />
        </motion.div>

        {/* Optional Bottom Left Driving Animation */}
        <motion.div
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 2 }}
          className="absolute bottom-0 left-10 w-28 sm:w-40"
        >
          <Lottie animationData={carAnimation} loop={true} />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold mb-6 leading-tight">
              Your Journey Across <br />
              <span className="text-green-400">Nigeria Starts Here</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-gray-200 leading-relaxed">
              From Lagos to Abuja, Kano to Port Harcourt – we handle flights, hotels,
              apartments, and ground transportation with unmatched expertise and care.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link 
                to="/flight-booking"
                className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-4 rounded-full shadow-lg inline-flex items-center space-x-3 group transition"
              >
                <FaPlane className="group-hover:translate-x-1 transition-transform" />
                <span>Book Your Flight</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a 
                href="#services"
                className="bg-white/20 hover:bg-white/30 text-white border border-white text-lg px-8 py-4 rounded-full shadow-md inline-flex items-center space-x-3 backdrop-blur-md transition"
              >
                <span>Explore Services</span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Animated Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="text-center mb-16"
    >
      <h2 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-4">
        Tailored Travel Services Across Nigeria
      </h2>
      <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
        From effortless flight bookings to stylish stays and smooth rides, we offer every service you need to explore Nigeria with comfort and confidence.
      </p>
    </motion.div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
      {services.map((service, index) => {
        const IconComponent = service.icon;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
          >
            <div className="relative h-52">
              <img 
                src={service.image} 
                alt={service.title}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
              />
              <div className={`absolute top-4 left-4 w-12 h-12 ${service.color} rounded-full flex items-center justify-center shadow-md`}>
                <IconComponent className="text-white text-xl" />
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-2 text-sm mb-6">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <FaCheck className="text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link 
                to={service.path}
                className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition duration-300 group"
              >
                <span>Book Now</span>
                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
</section>


      {/* About Section */}
      <section className="py-24 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-6">
          Why Choose Yowa Travels?
        </h2>
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          At Yowa Travels, we bring the heart of Nigeria to your journey. With years of local and international travel expertise, we go beyond bookings to offer personalized experiences that reflect your interests and preferences. Our strong partnerships and deep understanding of Nigeria’s diverse regions ensure you enjoy comfort, safety, and cultural richness on every trip.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          {[
            { title: 'Local Expertise', desc: 'In-depth knowledge of Nigerian destinations and traditions' },
            { title: '24/7 Support', desc: 'Always here to assist you, no matter the time or timezone' },
            { title: 'Best Prices', desc: 'Transparent pricing and unbeatable deals for all budgets' },
            { title: 'Verified Partners', desc: 'Trusted network of airlines, hotels, and drivers' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaCheck className="text-green-600 text-lg" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="relative"
      >
        <img 
          src="https://images.pexels.com/photos/30403149/pexels-photo-30403149.jpeg" 
          alt="Nigerian Tourism"
          className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
        />
        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-green-600 rounded-2xl flex items-center justify-center shadow-xl">
          <div className="text-center text-white">
            <p className="text-3xl font-bold">10k+</p>
            <p className="text-sm">Happy Travelers</p>
          </div>
        </div>
      </motion.div>
    </div>
  </div>
</section>

{/* NEW ILLUSTRATIVE SECTION */}
<section className="py-24 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <img 
          src='https://images.pexels.com/photos/31487832/pexels-photo-31487832/free-photo-of-zuma-rock-on-a-clear-day-in-abuja-nigeria.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2' 
          alt="Journey Illustration"
          className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          Your Journey, Our Priority
        </h3>
        <p className="text-lg text-gray-600 leading-relaxed mb-6">
          Whether you're planning a vacation, a business trip, or a spontaneous weekend getaway, Yowa Travels ensures your experience is seamless and enjoyable. We handle all the details so you can focus on what truly matters—enjoying the ride.
        </p>
        <ul className="space-y-3 text-sm text-gray-700">
          <li className="flex items-center gap-3">
            <FaCheck className="text-green-500" /> Flexible travel planning
          </li>
          <li className="flex items-center gap-3">
            <FaCheck className="text-green-500" /> Curated recommendations
          </li>
          <li className="flex items-center gap-3">
            <FaCheck className="text-green-500" /> Seamless check-ins & coordination
          </li>
        </ul>
      </motion.div>
    </div>
  </div>
</section>

      {/* Testimonials Section */}
      <section className="py-20 gradient-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              What Our Travelers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Read real experiences from thousands of satisfied customers who trusted 
              Yowa Travels for their Nigerian adventures.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-6">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-800">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.location}</p>
                    <div className="flex space-x-1 mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FaStar key={i} className="text-yellow-400 text-sm" />
                      ))}
                    </div>
                  </div>
                </div>
                <FaQuoteLeft className="text-green-500 text-2xl mb-4" />
                <p className="text-gray-700 leading-relaxed">{testimonial.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;